import {Link} from "react-router-dom";
import className from "classnames/bind";
import {FontAwesomeIcon} from "~/components";
import {useCan} from "~/hooks";
import {useGetProjectDashboardQuery} from "~/reduxs/api/projectApiSlice";
import style from "../style/Project.module.scss";

const cn = className.bind(style);

/**
 * Onboarding checklist của 1 dự án: ① Chạy crawl → ② Kết nối GSC → ③ Đo PageSpeed →
 * ④ Hỏi trợ lý AI. Đánh dấu ✓ theo DỮ LIỆU THẬT từ API dashboard (dùng chung query với
 * ProjectOverview — RTK Query tự dedupe). Ẩn hoàn toàn khi đã xong mọi bước user có quyền.
 */
function ProjectChecklist({projectId}) {

	const canCrawl = useCan('crawl');
	const canGsc = useCan('gsc');
	const canPageSpeed = useCan('pagespeed');
	const canAi = useCan('ai');

	const {data} = useGetProjectDashboardQuery(projectId);

	if (!data) return null;

	const steps = [
		{
			key: 'crawl',
			allowed: canCrawl,
			done: Boolean(data.crawl),
			icon: 'fa-light fa-spider',
			label: 'Chạy crawl đầu tiên',
			desc: 'Quét toàn site để chấm điểm SEO từng trang và phát hiện lỗi.',
			to: `/projects/${projectId}/crawler`,
			cta: 'Mở Site Crawler',
		},
		{
			key: 'gsc',
			allowed: canGsc,
			done: Boolean(data.gsc?.connected),
			icon: 'fa-light fa-magnifying-glass-chart',
			label: 'Kết nối Google Search Console',
			desc: 'Xem dữ liệu hiệu suất tìm kiếm thật: click, impression, từ khoá.',
			to: `/projects/${projectId}/gsc`,
			cta: 'Kết nối GSC',
		},
		{
			key: 'pagespeed',
			allowed: canPageSpeed,
			done: Boolean(data.pagespeed),
			icon: 'fa-light fa-gauge-high',
			label: 'Đo PageSpeed / Core Web Vitals',
			desc: 'Kiểm tra tốc độ tải và trải nghiệm trang trên mobile + desktop.',
			to: '/pagespeed',
			cta: 'Đo PageSpeed',
		},
		{
			key: 'ai',
			allowed: canAi,
			done: (data.ai?.conversations || 0) > 0 || (data.ai?.generations || 0) > 0,
			icon: 'fa-light fa-wand-magic-sparkles',
			label: 'Hỏi trợ lý AI',
			desc: 'Để AI phân tích dữ liệu crawl/GSC của dự án và gợi ý việc cần làm.',
			to: `/projects/${projectId}/ai`,
			cta: 'Mở trợ lý AI',
		},
	].filter((s) => s.allowed);

	const doneCount = steps.filter((s) => s.done).length;

	// Đã hoàn thành hết (hoặc không có bước nào trong quyền) → không chiếm chỗ nữa.
	if (steps.length === 0 || doneCount === steps.length) return null;

	return (
		<div className={cn('card', 'checklist')}>
			<div className={cn('checklistHead')}>
				<h4 className={cn('checklistTitle')}>
					<FontAwesomeIcon icon="fa-light fa-list-check" /> Bắt đầu với dự án này
				</h4>
				<span className={cn('checklistCount')}>{doneCount}/{steps.length} hoàn thành</span>
			</div>

			<div className={cn('checklistSteps')}>
				{steps.map((s, i) => (
					<div key={s.key} className={cn('checklistStep', {done: s.done})}>
						<span className={cn('stepMark')}>
							{s.done ? <FontAwesomeIcon icon="fa-solid fa-check" /> : i + 1}
						</span>
						<div className={cn('stepBody')}>
							<div className={cn('stepLabel')}>{s.label}</div>
							<div className={cn('stepDesc')}>{s.desc}</div>
						</div>
						{!s.done && (
							<Link to={s.to} className={cn('stepCta')}>
								{s.cta} <FontAwesomeIcon icon="fa-light fa-arrow-right" />
							</Link>
						)}
					</div>
				))}
			</div>
		</div>
	);
}

export default ProjectChecklist;
